import { createOrder } from '@/features/domain/order'
import {
  generateRecurrentOrders,
  getOrder,
  getUsedSpaceForecast,
  selectOrders,
} from '@/features/domain/order'
import { updatePlanningRange } from '@/features/domain/territory'
import { selectUserConfiguration } from '@/features/domain/user'
import { store, useAppDispatch } from '@/store'
import { Button, Stack } from '@mui/material'
import { addMonths, format, parse } from 'date-fns'
import { useSelector } from 'react-redux'

const orderTemplate: uui.domain.client.rm.OrderTemplate = {
  name: 'John Doe',
  companyId: null,
  priority: 0,
  forceVehicleId: null,
  loads: {},
  delivery: {
    location: {
      address: '34 North Ave, Elizabeth, NJ 07208-2497, United States',
      latLng: [40682010, -74215930],
      rooftopLatLng: [40681700, -74215830],
      source: 'H_GEO',
      status: 'OK',
      geoAddress: '34 North Ave, Elizabeth, NJ 07208-2497, United States',
    },
    barcodes: [],
    customFields: {},
    notes: '',
    serviceTimeSec: 0,
    tagsIn: [],
    tagsOut: [],
    timeWindowExceptions: {},
    timeWindows: [],
    depotId: null,
  },
  isService: false,
}

export function ServerDataEndpoints() {
  const dispatch = useAppDispatch()
  const { today } = useSelector(selectUserConfiguration)
  const todayDate = parse(today, 'yyyyMMdd', new Date())

  const handleGetUsedSpaceForecast = async () => {
    const newPlanningRange = 5 // Example value, adjust as needed
    const result = await dispatch(getUsedSpaceForecast({ newPlanningRange }))
    console.log(`getUsedSpaceForecast result (new range === ${newPlanningRange}):`, result.payload)
  }

  const handleGenerateRecurrentOrders = async () => {
    const result = await dispatch(generateRecurrentOrders())
    console.log('generateRecurrentOrders result:', result.payload)
  }

  const handleUpdatePlanningRange = async () => {
    const newPlanningRange = 8 // Example value, adjust as needed

    const result = await dispatch(
      updatePlanningRange({
        planningRange: newPlanningRange,
        exportDeletedOrders: true,
        cfg: {
          format: 'PDF',
        },
      }),
    )
    console.log(`updatePlanningRange result (new range === ${newPlanningRange}):`, result.payload)
  }
  const handleCreateSingleOrder = async () => {
    // Example order creation, adjust as needed
    const orderCreateRequest: uui.domain.ui.forms.SingleOrderCreate = {
      type: 'single-order',
      orderTemplate: {
        ...orderTemplate,
        eligibility: {
          type: 'on',
          dates: [today],
        },
        name: 'Single Order',
      },
    }

    const result = await dispatch(createOrder(orderCreateRequest))
    console.log('createOrder result:', result.payload)
  }

  const handleCreateDailyRecurrence = async () => {
    const orderCreateRequest: uui.domain.ui.forms.RecurrenceOrderCreate = {
      type: 'recurrence',
      recurrencePeriod: {
        recurrenceType: 'DAILY',
      },
      startDate: format(new Date(), 'yyyyMMdd'),
      recurrenceEnd: {
        endType: 'AFTER_NUMBER',
        numOfRecurrencesRequired: 5,
      },

      orderTemplate: { ...orderTemplate, name: `Rec. Daily, ends after 5 occurrences` },
    }

    const result = await dispatch(createOrder(orderCreateRequest))
    console.log('createRecurrence result:', result.payload)
  }

  const handleCreateMonthlyRecurrence = async () => {
    const date = format(addMonths(todayDate, 5), 'yyyyMMdd') // Example date, adjust as needed
    const orderCreateRequest: uui.domain.ui.forms.RecurrenceOrderCreate = {
      type: 'recurrence',
      recurrencePeriod: {
        recurrenceType: 'MONTHLY',
        period: 1, // Example period, adjust as needed
        when: 'ON_DATE',
        date,
      },
      startDate: format(new Date(), 'yyyyMMdd'),
      recurrenceEnd: {
        endType: 'NEVER',
      },

      orderTemplate: { ...orderTemplate, name: `Rec. Monthly ends on ${date}` },
    }

    const result = await dispatch(createOrder(orderCreateRequest))
    console.log('createRecurrence result:', result.payload)
  }

  const handleCreateAnnualRecurrence = async () => {
    const orderCreateRequest: uui.domain.ui.forms.RecurrenceOrderCreate = {
      type: 'recurrence',
      recurrencePeriod: {
        recurrenceType: 'ANNUALLY',
        period: 1, // Example period, adjust as needed
        when: 'ON_CARDINAL',
        cardinal: 1, // First week
        weekday: 1, // Monday
        month: 8, // August
      },
      startDate: format(new Date(), 'yyyyMMdd'),
      recurrenceEnd: {
        endType: 'NEVER',
      },

      orderTemplate: {
        ...orderTemplate,
        name: `Rec. Annually on first monday of August`,
      },
    }

    const result = await dispatch(createOrder(orderCreateRequest))
    console.log('createRecurrence result:', result.payload)
  }

  const handleGetOrder = async () => {
    const existingOrders = selectOrders(store.getState())
    if (Object.keys(existingOrders).length === 0) {
      console.warn('No existing orders found, cannot fetch orders without IDs.')
      return
    }

    // Fetch orders using the first order ID from the existing orders
    // Adjust this logic as needed to fetch specific orders
    const orderId = Object.keys(existingOrders)[0]
    const result = await dispatch(getOrder({ orderId, date: today }))
    console.log('getOrder result:', result.payload)
  }

  return (
    <Stack mt={3} spacing={2}>
      <Button variant="contained" onClick={handleGetUsedSpaceForecast}>
        Trigger getUsedSpaceForecast
      </Button>
      <Button variant="contained" onClick={handleGenerateRecurrentOrders}>
        Trigger generateRecurrentOrders
      </Button>
      <Button variant="contained" onClick={handleUpdatePlanningRange}>
        Trigger updatePlanningRange
      </Button>
      <Button variant="contained" onClick={handleCreateSingleOrder}>
        Trigger createOrder (single-order)
      </Button>
      <Button variant="contained" onClick={handleCreateDailyRecurrence}>
        Trigger createOrder (daily recurrence)
      </Button>
      <Button variant="contained" onClick={handleCreateMonthlyRecurrence}>
        Trigger createOrder (monthly recurrence)
      </Button>
      <Button variant="contained" onClick={handleCreateAnnualRecurrence}>
        Trigger createOrder (annually recurrence every first monday of the August)
      </Button>
      <Button variant="contained" onClick={handleGetOrder}>
        Trigger getOrder
      </Button>
    </Stack>
  )
}
